/**
 * Clear inline styles that conflict with CSS object-fit sizing.
 */
function resetHeybikeCardImages(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('img.heybike-mega-card__img').forEach((img) => {
    img.style.removeProperty('width');
    img.style.removeProperty('height');
    img.style.removeProperty('max-width');
    img.style.removeProperty('max-height');
  });
}

/**
 * Resolve heybike-drawer-nav and ensure it is initialized.
 */
function getHeybikeDrawerNav(fromNode) {
  const nav = fromNode?.closest?.('heybike-drawer-nav');
  if (!nav) return null;

  if (typeof customElements !== 'undefined' && customElements.upgrade) {
    customElements.upgrade(nav);
  }

  if (typeof nav.init === 'function') {
    nav.init();
  }

  return nav;
}

function isHeybikeDrawerOpen(nav) {
  const drawer = nav?.closest?.('side-drawer');
  if (!drawer) return false;
  return drawer.classList.contains('open') || drawer.classList.contains('active');
}

/**
 * HEYBIKE Mega Menu — desktop tab switching
 */
class HeybikeMegaMenu extends HTMLElement {
  connectedCallback() {
    this.onTabActivate = this.onTabActivate.bind(this);
    this.onResetImages = () => requestAnimationFrame(() => resetHeybikeCardImages(this));

    this.addEventListener('click', this.onTabActivate);
    this.addEventListener('mouseover', this.onTabActivate);
    resetHeybikeCardImages(this);

    this.detailsEl = this.closest('details');
    if (this.detailsEl) {
      this.menuObserver = new MutationObserver(() => {
        if (this.detailsEl.getAttribute('open') === 'true') {
          setTimeout(this.onResetImages, 0);
          setTimeout(this.onResetImages, 350);
        }
      });
      this.menuObserver.observe(this.detailsEl, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onTabActivate);
    this.removeEventListener('mouseover', this.onTabActivate);
    this.menuObserver?.disconnect();
  }

  onTabActivate(event) {
    const tab = event.target.closest('[data-category-tab]');
    if (!tab || !this.contains(tab)) return;

    if (event.type === 'click') {
      event.preventDefault();
      event.stopPropagation();
    }

    this.activate(tab.dataset.categoryId);
  }

  activate(categoryId) {
    if (!categoryId) return;

    const activeId = String(categoryId);

    this.querySelectorAll('[data-category-tab]').forEach((tab) => {
      const isActive = String(tab.dataset.categoryId) === activeId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.querySelectorAll('[data-category-panel]').forEach((panel) => {
      const isActive = String(panel.dataset.categoryId) === activeId;
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.onResetImages();
  }
}

if (!customElements.get('heybike-mega-menu')) {
  customElements.define('heybike-mega-menu', HeybikeMegaMenu);
}

/**
 * HEYBIKE Mobile Drawer — panel stack (Fig 1 → Fig 2)
 */
class HeybikeDrawerNav extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.drawerObserver?.disconnect();
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;

    this.drawerInner = this.closest('.heybike-drawer__inner');
    this.sideDrawer = this.closest('side-drawer');
    this.rootLevel = this.querySelector('[data-drawer-level="0"]');
    this.stack = this.rootLevel ? [this.rootLevel] : [];
    this._drawerWasOpen = isHeybikeDrawerOpen(this);

    this.bindAccordions();
    this.bindDrawerClose();
    this.updateSubpanelState();
    resetHeybikeCardImages(this);
  }

  findPanel(panelId) {
    if (!panelId) return null;
    const id = String(panelId);
    const panels = this.querySelectorAll('[data-drawer-panel]');
    for (const panel of panels) {
      if (panel.getAttribute('data-drawer-panel') === id) return panel;
    }
    return null;
  }

  bindDrawerClose() {
    if (!this.sideDrawer) return;

    this.drawerObserver = new MutationObserver(() => {
      const isOpen = isHeybikeDrawerOpen(this);

      if (isOpen && !this._drawerWasOpen) {
        this._drawerWasOpen = true;
        this.resetStack();
        return;
      }

      if (!isOpen && this._drawerWasOpen) {
        this._drawerWasOpen = false;
        this.resetStack();
      }
    });

    this.drawerObserver.observe(this.sideDrawer, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  bindAccordions() {
    this.addEventListener('toggle', (e) => {
      const details = e.target;
      if (!details.matches('.heybike-drawer-category') || !details.open) return;
      if (details.classList.contains('heybike-drawer-category--multi')) return;

      const panel = details.closest('[data-drawer-panel]');
      if (!panel) return;

      panel.querySelectorAll('.heybike-drawer-category').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }, true);
  }

  handleForward(trigger) {
    const panelId = trigger?.getAttribute?.('data-drawer-forward');
    const target = this.findPanel(panelId);
    if (!target) return;
    this.showPanel(target);
  }

  handleBack() {
    this.goBack();
  }

  handleShowMore(btn) {
    const content = btn.closest('.heybike-drawer-category__content');
    if (!content) return;

    const batchSize = parseInt(btn.dataset.batchSize, 10) || 4;
    const hiddenItems = content.querySelectorAll('.heybike-drawer-product[hidden]');

    for (let i = 0; i < batchSize && i < hiddenItems.length; i += 1) {
      hiddenItems[i].removeAttribute('hidden');
    }

    if (content.querySelectorAll('.heybike-drawer-product[hidden]').length === 0) {
      btn.hidden = true;
    }

    requestAnimationFrame(() => resetHeybikeCardImages(this));
  }

  resetShowMore() {
    this.querySelectorAll('.heybike-drawer-category__content').forEach((content) => {
      const grid = content.querySelector('[data-drawer-visible-limit]');
      if (!grid) return;

      const visibleLimit = parseInt(grid?.dataset.drawerVisibleLimit, 10) || 4;
      const items = content.querySelectorAll('.heybike-drawer-product');

      items.forEach((item, index) => {
        if (index >= visibleLimit) {
          item.setAttribute('hidden', '');
        } else {
          item.removeAttribute('hidden');
        }
      });

      const btn = content.querySelector('[data-drawer-show-more]');
      if (btn) {
        btn.hidden = items.length <= visibleLimit;
      }
    });
  }

  resetAccordions() {
    this.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
      const categories = panel.querySelectorAll('.heybike-drawer-category');
      const expandAll = panel.classList.contains('heybike-drawer-panel--nested');

      categories.forEach((details, index) => {
        details.open = expandAll || index === 0;
      });
    });
  }

  expandAllNestedCategories(panel) {
    if (!panel?.classList.contains('heybike-drawer-panel--nested')) return;

    panel.querySelectorAll('.heybike-drawer-category--multi').forEach((details) => {
      details.open = true;
    });
  }

  showPanel(panel) {
    if (this.stack[this.stack.length - 1] === panel) return;

    const current = this.stack[this.stack.length - 1];
    if (current) {
      current.hidden = true;
      current.classList.remove('is-active');
    }

    panel.hidden = false;
    panel.classList.add('is-active');
    this.stack.push(panel);
    panel.scrollTop = 0;
    this.expandAllNestedCategories(panel);
    this.updateSubpanelState();
    requestAnimationFrame(() => resetHeybikeCardImages(this));
  }

  goBack() {
    if (this.stack.length <= 1) return;

    const current = this.stack.pop();
    if (current) {
      current.hidden = true;
      current.classList.remove('is-active');
    }

    const prev = this.stack[this.stack.length - 1];
    if (prev) {
      prev.hidden = false;
      prev.classList.add('is-active');
    }

    this.updateSubpanelState();
    requestAnimationFrame(() => resetHeybikeCardImages(this));
  }

  resetStack() {
    if (!this._initialized) return;

    this.querySelectorAll('[data-drawer-panel]').forEach((panel) => {
      panel.hidden = true;
      panel.classList.remove('is-active');
    });

    if (this.rootLevel) {
      this.rootLevel.hidden = false;
      this.rootLevel.classList.add('is-active');
    }

    this.stack = this.rootLevel ? [this.rootLevel] : [];
    this.updateSubpanelState();
    this.resetShowMore();
    this.resetAccordions();
  }

  updateSubpanelState() {
    const isSubPanel = this.stack.length > 1;
    this.drawerInner?.classList.toggle('heybike-drawer__inner--subpanel', isSubPanel);
  }
}

if (!customElements.get('heybike-drawer-nav')) {
  customElements.define('heybike-drawer-nav', HeybikeDrawerNav);
}

function handleHeybikeDrawerInteraction(e) {
  const nav = getHeybikeDrawerNav(e.target);
  if (!nav) return;

  const forward = e.target.closest('[data-drawer-forward]');
  if (forward && nav.contains(forward)) {
    e.preventDefault();
    e.stopPropagation();
    nav.handleForward(forward);
    return;
  }

  const back = e.target.closest('[data-drawer-back]');
  if (back && nav.contains(back)) {
    e.preventDefault();
    e.stopPropagation();
    nav.handleBack();
    return;
  }

  const showMore = e.target.closest('[data-drawer-show-more]');
  if (showMore && nav.contains(showMore)) {
    e.preventDefault();
    e.stopPropagation();
    nav.handleShowMore(showMore);
  }
}

if (!window.__heybikeDrawerNavBound) {
  window.__heybikeDrawerNavBound = true;
  document.addEventListener('click', handleHeybikeDrawerInteraction, true);
  document.addEventListener('touchend', handleHeybikeDrawerInteraction, true);
}
